import { useEffect } from 'react';

import { isApiEnabled } from './config';
import { subscribeProfileQueue, syncProfile } from './profile-sync';

import type { UseProfileSyncDeps } from './types';

const BACKOFF_BASE_MS = 1_000;
const BACKOFF_CAP_MS = 300_000;
/** `online` / 포그라운드 복귀가 다음 시도를 앞당길 수 있는 최소 간격. */
const EVENT_RETRY_THROTTLE_MS = 60_000;

const defaultDeps: UseProfileSyncDeps = {
  enabled: isApiEnabled,
  sync: () => syncProfile(),
};

/**
 * 쌓아 둔 프로필 변경을 서버로 흘려보낸다 — 부팅에 한 번, 그 뒤엔 실패한 만큼만.
 *
 * `useDeviceIdentity` 와 같은 모양(단일비행·배가 백오프·이벤트 앞당김)이지만 **멈추는
 * 조건이 다르다**: 저쪽은 한 번 서면 끝이고, 이쪽은 `retry` 가 아닌 결과가 나오면
 * 보낼 것이 남지 않았다는 뜻이라 멈춘다. `dropped`(서버가 거절한 페이로드)도
 * 멈춤이다 — 같은 값을 다시 보내면 같은 답이 온다.
 *
 * 화면을 막지 않고 실패를 띄우지도 않는다. 로컬이 정본이라 서버가 늦어도 앱은 돈다.
 */
export const useProfileSync = (
  deps: UseProfileSyncDeps = defaultDeps,
): void => {
  useEffect(() => {
    if (!deps.enabled()) return;

    let cancelled = false;
    let settled = false;
    let inFlight = false;
    let attempt = 0;
    let lastAttemptAt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const clearTimer = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };

    const run = async () => {
      if (cancelled || settled || inFlight) return;
      inFlight = true;
      lastAttemptAt = Date.now();
      try {
        const outcome = await deps.sync();
        if (cancelled) return;
        if (outcome !== 'retry') {
          settled = true;
          clearTimer();
          return;
        }
        clearTimer();
        timer = setTimeout(
          () => void run(),
          Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_CAP_MS),
        );
        attempt += 1;
      } finally {
        inFlight = false;
      }
    };

    const retryNow = () => {
      if (settled || inFlight) return;
      if (Date.now() - lastAttemptAt < EVENT_RETRY_THROTTLE_MS) return;
      clearTimer();
      void run();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') retryNow();
    };

    /**
     * 새로 쌓인 값은 앞선 판정을 무효로 만든다 — 보낼 것이 없어 멈췄든, 4xx 로 포기했든
     * 이번 값은 아직 아무도 거절하지 않았다. 그래서 백오프까지 처음으로 되돌린다.
     */
    const onQueued = () => {
      settled = false;
      attempt = 0;
      clearTimer();
      void run();
    };

    window.addEventListener('online', retryNow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    const unsubscribe = subscribeProfileQueue(onQueued);
    void run();

    return () => {
      cancelled = true;
      clearTimer();
      unsubscribe();
      window.removeEventListener('online', retryNow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [deps]);
};
